import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { useNavigate } from "react-router-dom";
import { FileSpreadsheet, GitMerge, Landmark, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/FilterBar";
import { MultiUploadCard } from "@/components/MultiUploadCard";
import { FolderImport } from "@/components/FolderImport";
import { TxLogementSelect } from "@/components/TxLogementSelect";
import { aControler, ImportControleDialog } from "@/components/ImportControleDialog";
import { Panel } from "@/components/KpiCard";
import { LogementsAVerifier } from "@/components/LogementsAVerifier";
import { DeplacementsPanel } from "@/components/DeplacementsPanel";
import { HistoriqueReimports } from "@/components/HistoriqueReimports";
import { RecupererAnnonces } from "@/components/RecupererAnnonces";
import { useFilters } from "@/context/FilterContext";
import { api, cleanParams, errMsg, money } from "@/lib/api";

const SOURCES = [
  { type: "banque", title: "Relevé bancaire", hint: "Desjardins (AccèsD CSV, Excel ou relevé PDF). Un ou plusieurs fichiers.", icon: Landmark, accent: "text-emerald-700" },
  { type: "airbnb", title: "Rapport Airbnb", hint: "Historique des transactions : un fichier par appartement (OUEST / EST détecté depuis le nom) ou un seul fichier pour tous (colonne « Annonce »).", icon: FileSpreadsheet, accent: "text-[#FF385C]" },
  { type: "booking", title: "Rapport Booking", hint: "Un fichier par logement : indiquez le logement de chaque fichier (OUEST / EST détecté depuis le nom).", icon: FileSpreadsheet, accent: "text-[#003580]" },
];
const TYPE_LABEL = { banque: "Banque", airbnb: "Airbnb", booking: "Booking", facture_booking: "Facture Booking" };

const DeleteImport = ({ imp, onDone }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`delete-import-${imp.id}`}><Trash2 className="h-4 w-4" /></Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Supprimer cet import ?</AlertDialogTitle>
        <AlertDialogDescription>Les {imp.count} ligne(s) de « {imp.filename} » et leurs rapprochements seront supprimés.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="delete-import-cancel">Annuler</AlertDialogCancel>
        <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="delete-import-confirm"
          onClick={() => api.delete(`/imports/${imp.id}`).then(() => { toast.success("Import supprimé"); onDone(); }).catch((e) => toast.error(errMsg(e)))}>
          Supprimer
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const Th = ({ children, right }) => <th className={`py-2 px-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;

export default function Imports() {
  const { filters, refreshOptions, resetFilters, options } = useFilters();
  const [imports, setImports] = useState([]);
  const [txs, setTxs] = useState([]);
  const [controle, setControle] = useState([]);

  const load = useCallback(() => {
    api.get("/imports").then((r) => setImports(r.data));
    api.get("/transactions", { params: cleanParams({ mois: filters.mois }) }).then((r) => setTxs(r.data));
  }, [filters.mois]);

  useEffect(load, [load]);
  const [derniers, setDerniers] = useState(null);
  const [deplaces, setDeplaces] = useState(null);
  const [versionHisto, setVersionHisto] = useState(0);
  const refresh = (ids, dep) => {
    load(); refreshOptions();
    if (Array.isArray(ids)) { setDerniers(ids.length ? [...new Set(ids)] : null); setDeplaces(dep || null); }
  };
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const runRapprochement = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/rapprochement/auto");
      toast.success(`${data.propositions} correspondance(s) proposée(s) · ${data.groupes} virement(s) groupé(s)${data.factures ? ` · ${data.factures} facture(s) Booking` : ""}`);
      await refreshOptions().catch(() => {});
      resetFilters();
      navigate("/rapprochement");
    } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };

  return (
    <div data-testid="imports-page">
      <PageHeader eyebrow="Étape 1" title="Imports des relevés">
        <Button onClick={runRapprochement} disabled={running} className="bg-emerald-700 hover:bg-emerald-800" data-testid="imports-run-rapprochement-button">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitMerge className="h-4 w-4 mr-2" />}Lancer le rapprochement
        </Button>
      </PageHeader>
      <FolderImport onDone={refresh} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SOURCES.map((s) => <MultiUploadCard key={s.type} source={s} onDone={refresh} />)}
      </div>
      {deplaces && <DeplacementsPanel reimport={deplaces} onClose={() => setDeplaces(null)} onChange={() => { load(); setVersionHisto((v) => v + 1); }} />}
      <HistoriqueReimports cle={`${deplaces?.id || ""}-${versionHisto}`} onChange={load} />
      {derniers && <div className="mt-6"><LogementsAVerifier importIds={derniers} prefix="import-verif" onChange={load} /></div>}
      <div className="mt-6 grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Panel title="Historique des imports" className="xl:col-span-2" testId="imports-history">
          <RecupererAnnonces onDone={load} />
          {imports.length === 0 ? <p className="text-sm text-slate-500">Aucun import.</p> : (
            <ul className="divide-y divide-slate-100">
              {imports.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`import-row-${i.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{i.filename}</p>
                    <p className="text-xs text-slate-500">{TYPE_LABEL[i.type]} · {i.controle ? "contrôle des totaux" : `${i.count} ligne(s)`}{i.interets_ajoutes ? ` · ${i.interets_ajoutes} remboursement(s) de prêt` : ""}{i.doublons ? ` · ${i.doublons} doublon(s)` : ""} · {new Date(i.created_at).toLocaleDateString("fr-CA")}</p>
                    {i.logements_inconnus?.length > 0 && <p className="text-[11px] text-red-600" data-testid={`import-inconnus-${i.id}`}>Nom(s) d'annonce inconnu(s) : {i.logements_inconnus.map((x) => `« ${x.nom} » (${x.n})`).join(", ")}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {aControler(i) && (
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-emerald-700" data-testid={`import-controle-${i.id}`}
                        onClick={() => setControle([{ fichier: i.filename, inserted: i.count, ...i }])}>
                        {(i.controle?.nb_ecarts ?? i.ecarts?.length) ? `${i.controle?.nb_ecarts ?? i.ecarts.length} écart(s)` : "Contrôle"}
                      </Button>
                    )}
                    <DeleteImport imp={i} onDone={() => { setDerniers(null); refresh(); }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <ImportControleDialog resultats={controle} onClose={() => setControle([])} onChange={load} />
        </Panel>
        <Panel title={`Transactions bancaires (${txs.length}) · affectez vos dépenses à un logement`} className="xl:col-span-3" testId="transactions-panel">
          <div className="overflow-x-auto max-h-[420px]">
            <SmartTable id="transactions" className="w-full text-sm" data-testid="transactions-table">
              <thead className="sticky top-0 bg-white z-10"><tr><Th>Date</Th><Th>Description</Th><Th>Catégorie</Th><Th right>Débit</Th><Th right>Crédit</Th><Th>Charge de</Th></tr></thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tx-row-${t.id}`}>
                    <td className="py-2 px-2 tabular text-xs whitespace-nowrap">{t.date}</td>
                    <td className="py-2 px-2">{t.description}</td>
                    <td className="py-2 px-2 text-xs text-slate-500">{t.categorie}</td>
                    <td className="py-2 px-2 text-right tabular text-red-600">{t.debit ? money(t.debit) : ""}</td>
                    <td className="py-2 px-2 text-right tabular text-emerald-700">{t.credit ? money(t.credit) : ""}</td>
                    <td className="py-1 px-2">{t.debit > 0 && <TxLogementSelect tx={t} logements={options.logements} onChanged={load} />}</td>
                  </tr>
                ))}
              </tbody>
            </SmartTable>
          </div>
        </Panel>
      </div>
    </div>
  );
}
